import React, { createContext, useContext, useRef, useCallback } from 'react';

type ScreenshotFn = () => string | null;

interface ScreenshotContextType {
  takeScreenshot: ScreenshotFn;
  register: (fn: ScreenshotFn) => void;
  unregister: () => void;
}

const ScreenshotContext = createContext<ScreenshotContextType | null>(null);

export const ScreenshotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const screenshotFnRef = useRef<ScreenshotFn | null>(null);

  const register = useCallback((fn: ScreenshotFn) => {
    screenshotFnRef.current = fn;
  }, []);

  const unregister = useCallback(() => {
    screenshotFnRef.current = null;
  }, []);

  const takeScreenshot = useCallback(() => {
    if (screenshotFnRef.current) {
      return screenshotFnRef.current();
    }
    // eslint-disable-next-line no-console
    console.warn('No visualization registered to take a screenshot.');
    return null;
  }, []);

  return (
    <ScreenshotContext.Provider value={{ takeScreenshot, register, unregister }}>
      {children}
    </ScreenshotContext.Provider>
  );
};

export const useScreenshot = () => {
  const ctx = useContext(ScreenshotContext);
  if (!ctx) throw new Error('useScreenshot must be used within ScreenshotProvider');
  return { takeScreenshot: ctx.takeScreenshot };
};

export const useScreenshotRegistry = () => {
  const ctx = useContext(ScreenshotContext);
  if (!ctx) throw new Error('useScreenshotRegistry must be used within ScreenshotProvider');
  return { register: ctx.register, unregister: ctx.unregister };
};
